'use client';

import { useMemo } from 'react';

import { useTranslations } from 'next-intl';

import { PublicStrategy, TradedAssets, TradedAssetsPeriod } from '@/api/copy-trading/types';
import { useTradedAssets } from '@/api/copy-trading/useTradedAssets';
import { Select } from '@/blocks/components/Controls/Select';
import { PieChart } from '@/blocks/components/PieChart';
import { FormatAssetName, FormatPercent } from '@/blocks/formatters';
import typography from '@/styles/typography.module.scss';

import { Card } from '../../../../../Components/Card';
import styles from './styles.module.scss';

export function AssetsBreakdown(props: { strategy: PublicStrategy; tradedAssets: TradedAssets }) {
  const { tradedAssets, period, setPeriod } = useTradedAssets(props.strategy, props.tradedAssets);
  const tr = useTranslations();

  const MemoizedPeriodSelect = useMemo(
    () => (
      <Select
        value={period}
        onChange={(e) => {
          setPeriod(e.target.value as TradedAssetsPeriod);
        }}
        values={[
          {
            label: tr('pages.strategyPage.sections.main.periodSelect.labels.all'),
            value: TradedAssetsPeriod.ALL,
          },
          {
            label: tr('pages.strategyPage.sections.main.periodSelect.labels.week'),
            value: TradedAssetsPeriod.WEEK,
          },
          {
            label: tr('pages.strategyPage.sections.main.periodSelect.labels.month'),
            value: TradedAssetsPeriod.MONTH,
          },
        ]}
      />
    ),
    [period, setPeriod, tr],
  );

  if (!tradedAssets) {
    return null;
  }

  const list =
    tradedAssets.length === 0 ? null : (
      <ul className={styles.assetsBreakdownAssetsList}>
        {tradedAssets.map((stat, index) => {
          return (
            <li key={stat.symbol + index} className={typography.bodyM}>
              <div className={styles.assetsBreakdownAssetsListAssetColor}></div>
              <div className={styles.assetsBreakdownAssetsListAssetName}>
                <FormatAssetName assetName={stat.symbol} />
              </div>
              <span className={styles.assetsBreakdownAssetsListAssetUsagePct}>
                <FormatPercent value={stat.usagePercent} />
              </span>
            </li>
          );
        })}
      </ul>
    );

  const pieChartData = tradedAssets.map((asset) => asset.usagePercent);

  return (
    <Card
      title={tr.rich(
        'pages.strategyPage.sections.main.widgets.main.tabs.tradingData.widgets.tradedAssets.title',
      )}
      headingComponent={MemoizedPeriodSelect}
      className={styles.assetsBreakdown}
    >
      <div className={styles.assetsBreakdownInnerContainer}>
        <div className={styles.assetsBreakdownPieChartContainer}>
          <PieChart className={styles.pieChart} slices={pieChartData} diameter={200} />
        </div>
        <div>{list}</div>
      </div>
    </Card>
  );
}
